import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type {
  Meeting,
  MeetingInsert,
  MeetingUpdate,
  MeetingFilters,
  AllMeetingsFilters,
  QuorumInfo,
  AttendanceStats,
} from '../types'

// ===== QUERY KEYS =====
export const meetingKeys = {
  all: ['meetings'] as const,
  lists: () => [...meetingKeys.all, 'list'] as const,
  list: (filters: MeetingFilters) => [...meetingKeys.lists(), filters] as const,
  allList: (filters: AllMeetingsFilters) => [...meetingKeys.lists(), 'all', filters] as const,
  details: () => [...meetingKeys.all, 'detail'] as const,
  detail: (id: string) => [...meetingKeys.details(), id] as const,
  quorum: (id: string) => [...meetingKeys.all, 'quorum', id] as const,
  attendance: (id: string) => [...meetingKeys.all, 'attendance', id] as const,
}

// Select complet pour la résidence (utilisé dans les mutations pour ne pas perdre de données en cache)
const RESIDENCE_SELECT = 'id, name, residence_key, address, city, postal_code, total_shares'

// ===== HOOKS =====

/**
 * Liste des AG avec filtres (pour une résidence spécifique)
 */
export function useMeetings(filters: MeetingFilters) {
  return useQuery({
    queryKey: meetingKeys.list(filters),
    queryFn: async (): Promise<Meeting[]> => {
      let query = supabase
        .from('meetings')
        .select(`
          *,
          residence:residences(id, name),
          fiscal_year:fiscal_years(id, name, start_date, end_date)
        `)
        .eq('residence_id', filters.residence_id)
        .order('meeting_date', { ascending: false })

      if (filters.fiscal_year_id) {
        query = query.eq('fiscal_year_id', filters.fiscal_year_id)
      }

      if (filters.status) {
        query = query.eq('status', filters.status)
      }

      if (filters.meeting_type) {
        query = query.eq('meeting_type', filters.meeting_type)
      }

      if (filters.search) {
        query = query.ilike('title', `%${filters.search}%`)
      }

      const { data, error } = await query

      if (error) throw error
      return data as Meeting[]
    },
    enabled: !!filters.residence_id,
  })
}

/**
 * Liste de toutes les AG avec filtres optionnels (multi-résidence)
 */
export function useAllMeetings(filters: AllMeetingsFilters = {}) {
  return useQuery({
    queryKey: meetingKeys.allList(filters),
    queryFn: async (): Promise<Meeting[]> => {
      let query = supabase
        .from('meetings')
        .select(`
          *,
          residence:residences(id, name, residence_key),
          fiscal_year:fiscal_years(id, name, start_date, end_date),
          resolutions(id),
          participants:meeting_participants(id)
        `)
        .order('meeting_date', { ascending: false })

      // Filtre par résidence (optionnel)
      if (filters.residence_id) {
        query = query.eq('residence_id', filters.residence_id)
      }

      // Filtre par statut
      if (filters.status) {
        query = query.eq('status', filters.status)
      } else if (!filters.includePast) {
        // Par défaut, exclure les AG clôturées si includePast n'est pas true
        query = query.in('status', ['SCHEDULED', 'CONVOCATION_READY', 'CONVOKED', 'LIVE'])
      }

      // Filtre par type
      if (filters.meeting_type) {
        query = query.eq('meeting_type', filters.meeting_type)
      }

      // Recherche textuelle
      if (filters.search) {
        query = query.ilike('title', `%${filters.search}%`)
      }

      const { data, error } = await query

      if (error) throw error
      return (data ?? []) as unknown as Meeting[]
    },
  })
}

/**
 * Détail d'une AG avec résolutions et participants
 */
export function useMeeting(meetingId: string | undefined) {
  return useQuery({
    queryKey: meetingKeys.detail(meetingId!),
    queryFn: async (): Promise<Meeting> => {
      const { data, error } = await supabase
        .from('meetings')
        .select(`
          *,
          residence:residences(id, name, residence_key, address, city, postal_code, total_shares),
          fiscal_year:fiscal_years(id, name, start_date, end_date),
          resolutions(
            *,
            template:resolution_templates(id, code, title),
            distribution_key:distribution_keys(id, name),
            budget:budgets(id, name),
            supplier:suppliers(id, name, code)
          ),
          participants:meeting_participants(
            *,
            owner:owners(id, first_name, last_name),
            proxy:proxies(id, proxy_holder_name),
            correspondence_vote:correspondence_votes(id, received_at)
          ),
          bureau:meeting_bureau(
            *,
            owner:owners(id, first_name, last_name)
          )
        `)
        .eq('id', meetingId!)
        .single()

      if (error) throw error
      return data as Meeting
    },
    enabled: !!meetingId,
  })
}

/**
 * Créer une AG
 */
export function useCreateMeeting() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: MeetingInsert): Promise<Meeting> => {
      const { data: meeting, error } = await supabase
        .from('meetings')
        .insert(data)
        .select(`
          *,
          residence:residences(${RESIDENCE_SELECT}),
          fiscal_year:fiscal_years(id, name, start_date, end_date)
        `)
        .single()

      if (error) throw error
      return meeting as Meeting
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: meetingKeys.lists() })
      queryClient.setQueryData(meetingKeys.detail(data.id), data)
    },
  })
}

/**
 * Mettre à jour une AG
 */
export function useUpdateMeeting() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: MeetingUpdate
    }): Promise<Meeting> => {
      const { data: meeting, error } = await supabase
        .from('meetings')
        .update(data)
        .eq('id', id)
        .select(`
          *,
          residence:residences(${RESIDENCE_SELECT}),
          fiscal_year:fiscal_years(id, name, start_date, end_date)
        `)
        .single()

      if (error) throw error
      return meeting as Meeting
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: meetingKeys.lists() })
      queryClient.setQueryData(meetingKeys.detail(data.id), data)
    },
  })
}

/**
 * Supprimer une AG (uniquement si SCHEDULED)
 */
export function useDeleteMeeting() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from('meetings').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: meetingKeys.lists() })
    },
  })
}

/**
 * Valider l'ordre du jour (passer les résolutions à VOTING et l'AG à CONVOCATION_READY)
 */
export function useValidateAgenda() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (meetingId: string): Promise<Meeting> => {
      // 1. Passer toutes les résolutions DRAFT à VOTING
      const { error: resError } = await supabase
        .from('resolutions')
        .update({ status: 'VOTING' })
        .eq('meeting_id', meetingId)
        .eq('status', 'DRAFT')

      if (resError) throw resError

      // 2. Passer l'AG à CONVOCATION_READY
      const { data: meeting, error } = await supabase
        .from('meetings')
        .update({
          status: 'CONVOCATION_READY',
        })
        .eq('id', meetingId)
        .eq('status', 'SCHEDULED')
        .select(`
          *,
          residence:residences(${RESIDENCE_SELECT}),
          fiscal_year:fiscal_years(id, name, start_date, end_date)
        `)
        .single()

      if (error) throw error
      return meeting as Meeting
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: meetingKeys.lists() })
      queryClient.setQueryData(meetingKeys.detail(data.id), data)
      // Invalider aussi les résolutions car leur statut a changé
      queryClient.invalidateQueries({ queryKey: ['resolutions'] })
    },
  })
}

/**
 * Démarrer une AG (passer à LIVE)
 */
export function useStartMeeting() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string): Promise<Meeting> => {
      const { data: meeting, error } = await supabase
        .from('meetings')
        .update({
          status: 'LIVE',
          actual_start_time: new Date().toISOString(),
        })
        .eq('id', id)
        .eq('status', 'CONVOKED')
        .select(`
          *,
          residence:residences(${RESIDENCE_SELECT}),
          fiscal_year:fiscal_years(id, name, start_date, end_date)
        `)
        .single()

      if (error) throw error
      return meeting as Meeting
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: meetingKeys.lists() })
      queryClient.setQueryData(meetingKeys.detail(data.id), data)
    },
  })
}

/**
 * Clôturer une AG
 */
export function useCloseMeeting() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string): Promise<Meeting> => {
      const { data: meeting, error } = await supabase
        .from('meetings')
        .update({
          status: 'CLOSED',
          actual_end_time: new Date().toISOString(),
        })
        .eq('id', id)
        .eq('status', 'LIVE')
        .select(`
          *,
          residence:residences(${RESIDENCE_SELECT}),
          fiscal_year:fiscal_years(id, name, start_date, end_date)
        `)
        .single()

      if (error) throw error
      return meeting as Meeting
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: meetingKeys.lists() })
      queryClient.setQueryData(meetingKeys.detail(data.id), data)
    },
  })
}

/**
 * Calcul du quorum d'une AG
 */
export function useMeetingQuorum(meetingId: string | undefined) {
  return useQuery({
    queryKey: meetingKeys.quorum(meetingId!),
    queryFn: async (): Promise<QuorumInfo> => {
      // Récupérer les participants et leurs tantièmes
      const { data: participants, error } = await supabase
        .from('meeting_participants')
        .select(`
          *,
          units:meeting_participant_units(share_value)
        `)
        .eq('meeting_id', meetingId!)

      if (error) throw error

      // Récupérer le total des tantièmes de la résidence via les convoqués
      const { data: meeting } = await supabase
        .from('meetings')
        .select('total_shares_convoked, total_shares_present, total_shares_represented, total_shares_correspondence')
        .eq('id', meetingId!)
        .single()

      const totalShares = meeting?.total_shares_convoked || 0

      let presentShares = 0
      let representedShares = 0
      let correspondenceShares = 0

      for (const participant of participants || []) {
        const participantShares = (participant.units as { share_value: number }[])?.reduce(
          (sum, u) => sum + (u.share_value || 0),
          0
        ) || 0

        switch (participant.participation_mode) {
          case 'present':
            presentShares += participantShares
            break
          case 'proxy_given':
            representedShares += participantShares
            break
          case 'correspondence':
            correspondenceShares += participantShares
            break
        }
      }

      const totalPresentOrRepresented = presentShares + representedShares + correspondenceShares
      const quorumPercentage = totalShares > 0 ? (totalPresentOrRepresented / totalShares) * 100 : 0

      return {
        meeting_id: meetingId!,
        total_shares: totalShares,
        present_shares: presentShares,
        represented_shares: representedShares,
        correspondence_shares: correspondenceShares,
        quorum_reached: quorumPercentage > 0, // Le quorum n'est pas obligatoire en copropriété
        quorum_percentage: quorumPercentage,
      }
    },
    enabled: !!meetingId,
  })
}

/**
 * Statistiques de présence d'une AG
 */
export function useMeetingAttendance(meetingId: string | undefined) {
  return useQuery({
    queryKey: meetingKeys.attendance(meetingId!),
    queryFn: async (): Promise<AttendanceStats> => {
      const { data: participants, error } = await supabase
        .from('meeting_participants')
        .select(`
          participation_mode,
          units:meeting_participant_units(share_value)
        `)
        .eq('meeting_id', meetingId!)

      if (error) throw error

      const stats: AttendanceStats = {
        total_owners: participants?.length || 0,
        present_count: 0,
        represented_count: 0,
        correspondence_count: 0,
        absent_count: 0,
        total_shares: 0,
        present_shares: 0,
        represented_shares: 0,
        correspondence_shares: 0,
      }

      for (const participant of participants || []) {
        const participantShares = (participant.units as { share_value: number }[])?.reduce(
          (sum, u) => sum + (u.share_value || 0),
          0
        ) || 0

        stats.total_shares += participantShares

        switch (participant.participation_mode) {
          case 'present':
            stats.present_count++
            stats.present_shares += participantShares
            break
          case 'proxy_given':
            stats.represented_count++
            stats.represented_shares += participantShares
            break
          case 'correspondence':
            stats.correspondence_count++
            stats.correspondence_shares += participantShares
            break
          case 'absent':
            stats.absent_count++
            break
        }
      }

      return stats
    },
    enabled: !!meetingId,
  })
}
